import {Link, useLocation, useParams} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import {uniqid} from "../../../common/utils/function";
import {addressService} from "../../../store/actions";
import BCopy from "../../../components/b-copy";
import {useTranslation} from "react-i18next";
import {useAccount} from 'wagmi';
import {ethers, Signer} from "ethers";
import useCommon from "../../../common/hooks/useCommon";
import {Button, Modal} from 'react-bootstrap';

interface AbiItem {
    name: string;
    method_id: string;
    inputs: any[];
    stateMutability: string;
    outputs: any[];
    type: string;
}

const TransactionModal = (props: {
    show: boolean;
    onHide: () => void;
    txnDetails: { from: string; to: string; status: string; amount: string; time: number } | null;
}) => {
    return (
        <Modal show={props.show} onHide={props.onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Transaction Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {props.txnDetails ? (
                    <div>
                        <p>From: {props.txnDetails.from}</p>
                        <p>To: {props.txnDetails.to}</p>
                        <p>Amount: {props.txnDetails.amount} wei</p>
                        <p>Status: {props.txnDetails.status}</p>
                        <p>Time Taken: {props.txnDetails.time} ms</p>
                    </div>
                ) : (
                    <p>No transaction details available.</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={props.onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ContractMethodItem = (props: {
    data: AbiItem;
    metamaskAddress?: string;
    index: number;
    isRead?: boolean;
    allExpanded: boolean;
}) => {
    const myRef = useRef(null);
    const location = useLocation();
    const hashSplit = location.hash.split("#F");
    const {address: accountAddress} = useAccount();
    const account = useAccount();
    const {address} = useParams();
    const {data, index, allExpanded} = props;
    const [inputsValue, setInputsValue] = useState<string[]>(
        [...new Array(data.inputs?.length)].map(() => "")
    );
    const [multipleValue, setMultipleValue] = useState({
        write: {},
        read: {},
    } as any);
    const [amount, setAmount] = useState("");
    const [error, setError] = useState("");
    const [result, setResult] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState("");
    const {t, i18n} = useTranslation();
    const [signer, setSigner] = useState<Signer | undefined>(undefined);
    const [requestPending, setRequestPending] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [txnDetails, setTxnDetails] = useState<{ from: string, to: string, status: string, amount: string, time: number } | null>(null);
    const {get0x} = useCommon();

    const convertAddressPrefix = (address: string) => {
        if (address.startsWith("xdc")) {
            return "0x" + address.slice(3);
        }
        return address;
    };

    const setInputValue = (value: string, idx: number) => {
        const newArr = inputsValue.map((v, i) => (i === idx ? value : v));
        setInputsValue(newArr);
    };

    const handleRead = () => {
        const convertedInputs = inputsValue.map(convertAddressPrefix);
        setLoading(true);
        const path = "/query-read-method?is_custom_abi=false";

        const requestData = {
            from: accountAddress ?? "0x0000000000000000000000000000000000000000",
            args: convertedInputs,
            contract_type: "regular",
            method_id: data?.method_id,
        };

        addressService
            .queryRead(address, path, requestData)
            .then((response) => {
                setResult(response?.result);
                setLoading(false);
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (account.isConnected && window.ethereum) {
            const provider = new ethers.BrowserProvider(window.ethereum);
            const fetchSigner = async () => {
                if (requestPending) return;
                setRequestPending(true);
                try {
                    await provider.send("eth_requestAccounts", []);
                    const signer = await provider.getSigner();
                    setSigner(signer);
                } catch (e: any) {
                    setError(e.message);
                } finally {
                    setRequestPending(false);
                }
            };
            fetchSigner();
        } else {
            console.error("Please Install MetaMask!");
        }
    }, [account.isConnected]);

    const handleWrite = async () => {
        if (!account.isConnected) {
            setError("No wallet connected. Please connect your wallet.");
            return;
        }
        if (!signer) {
            setError("No signer available. Ensure MetaMask is connected and authorized.");
            return;
        }

        const convertedInputs = inputsValue.map(convertAddressPrefix);
        const contractAddress = address as string;
        const abi = [data];
        const contract = new ethers.Contract(contractAddress, abi, signer);

        try {
            setLoading(true);
            const startTime = performance.now();

            const tx = await contract[data.name](...convertedInputs, {
                value: data.stateMutability === "payable"
                    ? ethers.parseUnits(amount, "wei")
                    : undefined,
            });

            const receipt = await tx.wait();
            const endTime = performance.now();
            const timeTaken = Math.round(endTime - startTime);

            setTxnDetails({
                from: receipt.from,
                to: receipt.to,
                status: receipt.status === 1 ? "Success" : "Failed",
                amount: ethers.formatUnits(receipt.value.toString(), "wei"),
                time: timeTaken,
            });

            setResult(`Transaction Hash: ${tx.hash}`);
            setShowModal(true);
        } catch (error: any) {
            console.error("Error during contract write operation:", error);
            if (error instanceof Error) {
                setError(`Error: ${error.message}. Please ensure the contract conditions are met.`);
            } else {
                setError('An unknown error occurred.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Number(hashSplit?.[1]) - 1 === index) {
            // @ts-ignore
            myRef?.current?.scrollIntoView();
            setHighlight("h_active");
            setTimeout(() => setHighlight(""), 5000);
        }
    }, [hashSplit, index]);

    return (
        <div className={`accordion-item mb-4 ${highlight}`}>
            <h2 className="accordion-header">
                <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#${data.name || data.type}`}
                    aria-expanded={allExpanded || (Number(hashSplit?.[1]) - 1 === index)}
                    aria-controls={data.name || data.type}
                >
                    {index + 1}. {data.name || data.type}{" "}
                    {`(0x${data?.method_id})`}
                </button>
            </h2>

            <div
                id={data.name || data.type}
                className={`accordion-collapse collapse ${allExpanded ||
                Number(hashSplit?.[1]) - 1 === index ? "show" : ""
                }`}
                ref={myRef}
            >
                <div className="accordion-body">
                    {data.stateMutability === "payable" ? (
                        <div className="mb-3">
                            <p>
                                {t("Send native")} {process.env.REACT_APP_BASE_UNIT} (uint256)
                            </p>
                            <input
                                className="form-control"
                                value={amount}
                                placeholder={`uint256`}
                                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                                    setAmount(evt.currentTarget.value)
                                }
                            />
                        </div>
                    ) : null}
                    {data.inputs && data.inputs.length ? (
                        <div>
                            {data.inputs.map((input, idx) => {
                                const name = input.name || "<input>";
                                const isArrayValue = input.type.indexOf("[]") >= 0;
                                const tabMethod = props.isRead ? "read" : "write";

                                const itemValue = isArrayValue
                                    ? multipleValue[tabMethod][idx] || [{value: "", id: "1"}]
                                    : inputsValue[idx];

                                const itemType = input.type.slice(0, input.type.indexOf("[]"));

                                return (
                                    <div key={idx} className="mb-3">
                                        {isArrayValue ? (
                                            <div>
                                                <p>{name} <span>({input.type})</span></p>
                                                {itemValue.map(
                                                    (
                                                        item: { id: string; value: string },
                                                        itemId: number
                                                    ) => {
                                                        return (
                                                            <div className="mb-3 d-flex" key={item.id}>
                                                                <p style={{marginRight: "10px"}}>{itemId}.</p>
                                                                <input
                                                                    className="form-control"
                                                                    value={item.value}
                                                                    placeholder={`${itemType}`}
                                                                    onChange={(
                                                                        evt: React.ChangeEvent<HTMLInputElement>
                                                                    ) => {
                                                                        itemValue[itemId].value =
                                                                            evt.currentTarget.value;

                                                                        setMultipleValue({
                                                                            ...multipleValue,
                                                                            [tabMethod]: {
                                                                                ...multipleValue[tabMethod],
                                                                                [idx]: itemValue,
                                                                            },
                                                                        });

                                                                        setInputValue(
                                                                            itemValue.map((t: any) => t.value),
                                                                            idx
                                                                        );
                                                                    }}
                                                                />
                                                                {itemValue.length === 1 ? null : (
                                                                    <button
                                                                        className="btn btn-light"
                                                                        style={{marginLeft: "10px"}}
                                                                        onClick={() => {
                                                                            setMultipleValue({
                                                                                ...multipleValue,
                                                                                [tabMethod]: {
                                                                                    ...multipleValue[tabMethod],
                                                                                    [idx]: itemValue.filter(
                                                                                        (removeItem: any) =>
                                                                                            removeItem.id !== item.id
                                                                                    ),
                                                                                },
                                                                            });
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                                <button
                                                    className="btn btn-light mb-3"
                                                    style={{marginTop: "10px"}}
                                                    onClick={() => {
                                                        itemValue.push({value: "", id: uniqid()});

                                                        setMultipleValue({
                                                            ...multipleValue,
                                                            [tabMethod]: {
                                                                ...multipleValue[tabMethod],
                                                                [idx]: itemValue,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    + {t("Add one more")}
                                                </button>
                                            </div>
                                        ) : (
                                            <AmountInput
                                                name={name}
                                                type={input.type}
                                                value={inputsValue[idx]}
                                                onChange={(value) => setInputValue(value, idx)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                    {data.inputs?.length || data.stateMutability !== "view" ? (
                        <div className="mb-3">
                            {loading ? (
                                <div>{t("Loading")}</div>
                            ) : data.stateMutability === "view" ? (
                                <Button onClick={handleRead}>
                                    {t("Read")}
                                </Button>
                            ) : (
                                <Button
                                    disabled={!accountAddress}
                                    onClick={handleWrite}
                                >
                                    {t("Write")}
                                </Button>
                            )}
                        </div>
                    ) : null}
                    {!!data?.inputs?.length && (
                        <i className="bi bi-arrow-return-right"></i>
                    )}
                    <Outputs data={result?.output || data.outputs}/>
                    {error && (() => {
                        const errorMessage = error ?? 'No error message available';
                        const errorWords = errorMessage.split(' ');

                        const reasonIndex = errorWords.findIndex(word => word.includes('reason='));
                        const reasonPart = reasonIndex > -1 ? errorWords[reasonIndex] : '';
                        const rejected = reasonPart.split('=')[1]?.replace(/\"|,/, '').trim() || 'rejected';

                        const fromIndex = errorWords.findIndex(word => word.includes('"from"'));
                        const fromAddress = fromIndex > -1 ? errorWords[fromIndex + 1].replace(/\"|,/, '').trim() : 'N/A';
                        const toIndex = errorWords.findIndex(word => word.includes('"to"'));
                        const toAddress = toIndex > -1 ? errorWords[toIndex + 1].replace(/\"|,/, '').trim() : 'N/A';
                        const valueIndex = errorWords.findIndex(word => word.includes('"data"'));
                        const data = valueIndex > -1 ? errorWords[valueIndex + 1].replace(/\"|,/, '').trim() : 'N/A';

                        const detailsStartIndex = errorWords.indexOf('MetaMask');
                        const details = detailsStartIndex > -1
                            ? errorWords.slice(detailsStartIndex, detailsStartIndex + 8).join(' ').replace(/"}/, '').trim()
                            : 'Details not available';

                        const versionIndex = errorWords.findIndex(word => word.includes('version='));
                        const version = versionIndex > -1
                            ? errorWords[versionIndex].split('=')[1]?.replace(')', '').trim()
                            : 'Unknown';

                        return (
                            <div>
                                <p style={{marginTop: 5}} className="text-danger">
                                    User {rejected.replace(/",/, '').trim()} the request.
                                </p>
                                <p style={{marginTop: 5}} className="text-danger">
                                    Request Arguments:<br/>
                                    from: {fromAddress.replace(/",/, '').trim()}<br/>
                                    to: {toAddress.replace(/"/, '').trim()}<br/>
                                    value: {inputsValue[1]}<br/>
                                    Data: {data.replace(/",/, '').trim()}
                                </p>
                                <p style={{marginTop: 5}} className="text-danger">
                                    Details: {details.replace(/" },/, '').trim()}<br/>
                                    Version: ethers@{version}
                                </p>
                            </div>
                        );
                    })()
                    }
                    {txnDetails && (
                        <p style={{marginTop: 5}} className="text-success">
                            <Link to={`/address/${get0x(txnDetails.from)}`}>
                                From: {txnDetails.from}<br/>
                            </Link>
                            <Link to={`/address/${get0x(txnDetails.to)}`}>
                                To: {txnDetails.to}<br/>
                            </Link>
                            Status: {txnDetails.status}
                        </p>
                    )}
                    <TransactionModal
                        show={showModal}
                        onHide={() => setShowModal(false)}
                        txnDetails={txnDetails}
                    />
                </div>
            </div>
            <span className="h_copy">
                <BCopy value={data.name} tooltip={`${t("Method Name")}`}/>
            </span>
            <span className="h_link">
                <BCopy
                    value={
                        window.location.origin +
                        window.location.pathname +
                        hashSplit?.[0] +
                        `#F${1 + index}`
                    }
                    icon="bi-link"
                    tooltip={`${t("Permalink")}`}
                />
            </span>
        </div>
    );
};

const Outputs = ({data}: any) => {
    return (
        <p>
            {data && data.length > 0 ? (
                data.map((item: any, index: number) => {
                    const {type = '', name = '', value = ''} = item;

                    return (
                        <React.Fragment key={index}>
                            <span>{name} </span>
                            <span>
                                {type === "uint256" ? (
                                    <React.Fragment>
                                        {`(${type}) :`} <Link to={`/unitconverter?wei=${value}`}>{` ${value}`}</Link>
                                    </React.Fragment>
                                ) : (
                                    `${name ? `(${type})` : type} ${value && `: ${value}`}`
                                )}
                            </span>
                            {index < data.length - 1 && <span>, </span>}
                        </React.Fragment>
                    );
                })
            ) : (
                <span></span>
            )}
        </p>
    );
};

const AmountInput = ({name, type, value, onChange}: { name: string, type: string, value: string, onChange: (value: string) => void }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedOption, setSelectedOption] = useState("");
    const [customZeroes, setCustomZeroes] = useState("");

    const handleClose = () => {
        setShowModal(false);
        setSelectedOption("");
        setCustomZeroes("");
    };
    const handleShow = () => setShowModal(true);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedOption(e.target.value);
        if (e.target.value !== "custom") {
            setCustomZeroes("");
        }
    };

    const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (!value || (Number(value) <= 100 && /^\d+$/.test(value))) {
            setCustomZeroes(value);
        }
    };

    const handleAdd = () => {
        let zeroes = "1";
        if (selectedOption === "custom") {
            zeroes = "1" + "0".repeat(Number(customZeroes));
        } else {
            zeroes = selectedOption;
        }
        onChange(String(BigInt(value || "1") * BigInt(zeroes)));
        handleClose();
    };

    return <React.Fragment>
        <p className="d-flex align-items-center mb-2">
            {name} <span>({type})</span>
            {name === "amount" && (
                <button className="btn btn-sm btn-primary ms-2" onClick={handleShow}>
                    <i className="bi bi-plus"></i>
                </button>
            )}
        </p>
        <input
            className="form-control mb-3"
            value={value}
            placeholder={`${name} (${type})`}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                onChange(evt.currentTarget.value);
            }}
        />

        <Modal show={showModal} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Add Zeroes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <select className="form-select" value={selectedOption} onChange={handleSelectChange}>
                        <option value="">Select</option>
                        <option value="1000000">10⁶</option>
                        <option value="100000000">10⁸</option>
                        <option value="1000000000000000000">10¹⁸</option>
                        <option value="custom">Custom</option>
                    </select>
                </div>
                {selectedOption === "custom" && (
                    <div className="mb-3">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Enter number of zeroes (max 100)"
                            value={customZeroes}
                            onChange={handleCustomChange}
                            min="0"
                            max="100"
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleAdd}
                    disabled={!selectedOption || (selectedOption === "custom" && !customZeroes)}
                >
                    Add
                </Button>
            </Modal.Footer>
        </Modal>
    </React.Fragment>;
};

export default ContractMethodItem;
